// @ts-strict-ignore
import React, { useMemo, useState } from 'react'
import { merge } from 'lodash-es'
import { DataTable, useSidePanel } from '@node-space/ui-components'
import { useTransactionsContext } from 'hooks/context/useTransactionsContext'
import { useWalletContext } from 'hooks/context/useWalletContext'
import { useWalletTransactionDetailsQuery } from 'hooks/queries/useWalletTransactionDetailsQuery'
import { RefundSheet } from 'pages/Wallets/components/Refunds/components/RefundSheet'
import { getIsPartiallyRefundable } from 'pages/Wallets/components/Refunds/utils'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { Transaction } from 'types/transaction'
import useTransactionTableData from './hooks/useTransactionTableData'
import { SidePanelSummary } from './SidePanel/SidePanelSummary'

export const TransactionTable = () => {
  const transactions = useTransactionsContext()
  const wallet = useWalletContext()

  const [isRefundSheetOpen, setIsRefundSheetOpen] = useState(false)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | undefined>()
  const [isPartiallyRefundable, setIsPartiallyRefundable] = useState(false)

  const {
    isSidePanelOpen: isTransactionDetailsSidepanelOpen,
    sidePanelData: transactionDetails,
    paging: transactionsPaging,
    setIsSidePanelOpen: setIsTransactionDetailsSidepanelOpen,
    updateSidePanel: updateTransactionDetailsSidepanel,
  } = useSidePanel(transactions)

  const matchedTransaction =
    transactions?.find(
      transaction =>
        transaction?.id === transactionDetails?.id && transaction?.hash === transactionDetails?.hash
    ) || transactionDetails

  const transactionUUID = matchedTransaction?.context?.externalReference
  const { data: transactionDetailsById, isPending: isTransactionDetailsLoading } =
    useWalletTransactionDetailsQuery(transactionUUID)

  const selectedTransactionDetails = useMemo(() => {
    if (!matchedTransaction) return
    if (!transactionDetailsById) return matchedTransaction

    const merged = merge({}, matchedTransaction, transactionDetailsById) as Transaction
    merged.type = matchedTransaction.type
    merged.context = matchedTransaction.context
    return merged
  }, [matchedTransaction, transactionDetailsById])

  const openSidePanelSummary = (index: number) => {
    updateTransactionDetailsSidepanel(index)
    setIsTransactionDetailsSidepanelOpen(true)
  }

  const openRefundSheet = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    if (!transaction) {
      return
    }

    const isPartiallyRefundable = getIsPartiallyRefundable(transaction)
    setIsPartiallyRefundable(isPartiallyRefundable)

    setIsRefundSheetOpen(true)
  }

  const { columns, tableData } = useTransactionTableData({
    transactions,
    openSidePanelSummary,
    openRefundSheet,
  })

  return (
    <>
      <DataTable name="SimpleTable" headers={columns} data={tableData} />
      {!!selectedTransactionDetails && (
        <SidePanelSummary
          isOpen={isTransactionDetailsSidepanelOpen}
          paging={transactionsPaging}
          onClose={() => setIsTransactionDetailsSidepanelOpen(false)}
          transactionDetails={selectedTransactionDetails}
          isTransactionDetailsLoading={!!transactionUUID && isTransactionDetailsLoading}
        />
      )}
      {isRefundSheetOpen && (
        <>
          <RefundSheet
            wallet={wallet}
            transaction={selectedTransaction}
            isRefundSheetOpen={isRefundSheetOpen}
            setIsRefundSheetOpen={setIsRefundSheetOpen}
            setIsSheetOpen={setIsSheetOpen}
            isPartiallyRefundable={isPartiallyRefundable}
          />
          <WalletSheet isSheetOpen={isSheetOpen} />
        </>
      )}
    </>
  )
}
